// utils.js
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

export const ReservationStatusType = [
    { value: null, label: "전체" },
    { value: "PENDING", label: "예약접수" },
    { value: "RESERVATION_FIX", label: "예약완료" },
    { value: "CANCEL", label: "예약취소" },
    { value: "FINISH", label: "점검완료" },
];

export const useFormInput = (initialState) => {
    const [formData, setFormData] = useState({
        ...initialState,
        toastMsg: "",
        confirmStatus: {
            confirmPopupStatus: false,
            confirmTitle: "",
            confirmDescription: "",
            confirmButtons: [],
        },
    });

    // 재귀적으로 중첩된 객체 업데이트 함수
    const updateNestedField = (obj, keys, value) => {
        if (keys.length === 1) {
            return { ...obj, [keys[0]]: value || "" };
        }
        const key = keys[0];
        return {
            ...obj,
            [key]: updateNestedField(obj[key] || {}, keys.slice(1), value),
        };
    };

    const handleChange = (e) => {
        const target = e.target || e;
        const value = target.type === "radio" ? target.value : target.value || e.option?.value;
        const inputName = target.name; // 예: "contract.contractBusinessInfo.corpName"
        if (inputName.includes(".")) {
            const keys = inputName.split(".");
            setFormData((prevState) => updateNestedField(prevState, keys, value));
        } else {
            setFormData((prevState) => ({
                ...prevState,
                [inputName]: value || "",
            }));
        }
    };

    const setFormDataFunc = (name, value) => {
        setFormData((prevState) => ({
            ...prevState,
            [name]: value || "",
        }));
    };

    return {
        formData,
        handleChange,
        setFormDataFunc,
        setFormData,
    };
};

/**
 * 리스트 페이지용 필터 훅 - URL 쿼리 파라미터 자동 동기화
 * @param {object} initialFilter - 기본 필터 설정 (pageFilter)
 * @param {object} additionalState - 추가 상태 (toastMsg 등)
 */
export const useListPageFilter = (initialFilter, additionalState = {}) => {
    const [searchParams, setSearchParams] = useSearchParams();

    // URL 쿼리 파라미터를 객체로 변환
    const getParamsFromUrl = () => {
        const params = {};
        // 숫자 변환에서 제외할 필드들 (문자열로 유지해야 하는 필드들)
        const stringFields = ["query", "serialNumber", "carNumber", "name", "phone", "address", "tab"];

        for (const [key, value] of searchParams.entries()) {
            // 문자열로 유지해야 하는 필드들은 변환하지 않음
            if (stringFields.includes(key)) {
                params[key] = value;
            } else if (!isNaN(value) && value !== "") {
                // 숫자 형태의 값은 number로 변환
                params[key] = Number(value);
            } else {
                params[key] = value;
            }
        }
        return params;
    };

    // 초기 상태: URL 파라미터와 기본 필터 병합
    const [formData, setFormData] = useState(() => {
        const urlParams = getParamsFromUrl();
        return {
            ...additionalState,
            parameters: {
                ...initialFilter.parameters,
                ...urlParams, // URL 파라미터가 기본값보다 우선
            },
            toastMsg: "",
        };
    });

    const { parameters, toastMsg } = formData;

    // formData.parameters 변경 시 URL 업데이트
    useEffect(() => {
        const newSearchParams = new URLSearchParams();
        Object.entries(parameters).forEach(([key, value]) => {
            if (value !== "" && value !== null && value !== undefined) {
                newSearchParams.set(key, value.toString());
            }
        });
        setSearchParams(newSearchParams);
    }, [parameters, setSearchParams]);

    // URL 변경 시 formData.parameters 업데이트 (뒤로가기/앞으로가기 대응)
    useEffect(() => {
        const urlParams = getParamsFromUrl();
        const hasChanges = Object.keys(urlParams).some((key) => urlParams[key] !== parameters[key]);

        if (hasChanges) {
            setFormData((prev) => ({
                ...prev,
                parameters: {
                    ...initialFilter.parameters,
                    ...urlParams,
                },
            }));
        }
    }, [searchParams]);

    const handleChange = (e) => {
        const target = e.target || e;
        const value = target.type === "radio" ? target.value : target.value || e.option?.value;
        const inputName = target.name;

        setFormData((prevState) => ({
            ...prevState,
            [inputName]: value || "",
        }));
    };

    return {
        formData,
        handleChange,
        setFormData,
        parameters,
        toastMsg,
    };
};

export const getQueryParams = (query) => {
    return new URLSearchParams(query);
};

export const setQueryParams = (navigate, params) => {
    const searchParams = new URLSearchParams(params);
    navigate({ search: searchParams.toString() });
};

export const handleCommonFileChange = async (e, uploadType, setFormData, uploadFile) => {
    const fileObj = e.target.files[0];
    if (!fileObj) return;
    try {
        const formDataObj = new FormData();
        formDataObj.append("file", fileObj);
        const res = await uploadFile(formDataObj).unwrap();
        const newUploadId = res.data?.id || 0;
        setFormData((prev) => {
            const updatedUploads = [...(prev.contract?.contractGroup?.contractUploads || [])];
            const idx = updatedUploads.findIndex((x) => x.uploadType === uploadType);
            if (idx >= 0) {
                updatedUploads[idx] = {
                    ...updatedUploads[idx],
                    upload: {
                        id: newUploadId,
                        fileName: fileObj.name,
                        objectUrl: res.data?.objectUrl,
                    },
                };
            } else {
                updatedUploads.push({
                    uploadType,
                    upload: {
                        id: newUploadId,
                        fileName: fileObj.name,
                        objectUrl: res.data?.objectUrl,
                    },
                });
            }
            return {
                ...prev,
                contract: {
                    ...prev.contract,
                    contractGroup: {
                        ...prev.contract?.contractGroup,
                        contractUploads: updatedUploads,
                    },
                },
            };
        });
    } catch (error) {
        console.error(error);
        setFormData((prev) => ({
            ...prev,
            toastMsg: error?.data?.message || "파일 업로드 실패",
        }));
    }
};

/**
 * 날짜 유효성 검사 (YYYY-MM-DD 또는 YYYYMMDD)
 */
export function isValidDateString(dateStr) {
    if (!dateStr) return false;
    let y, m, d;
    if (dateStr.includes("-")) {
        [y, m, d] = dateStr.split("-");
    } else if (dateStr.length === 8) {
        y = dateStr.slice(0, 4);
        m = dateStr.slice(4, 6);
        d = dateStr.slice(6, 8);
    } else {
        return false;
    }
    y = Number(y);
    m = Number(m);
    d = Number(d);
    if (!y || !m || !d) return false;
    const date = new Date(y, m - 1, d);
    return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
}
